/**
 * Visit tracking service for analytics
 */

let sessionId = null;

// Generate a unique session ID
const generateSessionId = () => {
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    console.log('🆔 NEW Session ID generated:', sessionId);
  }
  return sessionId;
};

// Export function to check current sessionId
export const getCurrentSessionId = () => {
  console.log('🔍 Current Session ID:', sessionId);
  return sessionId;
};

// Export function to clear sessionId (for logout)
export const clearSessionId = () => {
  console.log('🗑️ Clearing Session ID:', sessionId);
  sessionId = null;
};

/**
 * Track a page visit
 */
export const trackVisit = async (page = window.location.pathname, userId = null, userEmail = null) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || '';
    
    const visitData = {
      page,
      userAgent: navigator.userAgent,
      referrer: document.referrer,
      sessionId: generateSessionId(),
      userId,
      userEmail
    };

    console.log('📤 Sending visit data to backend:', visitData);

    const response = await fetch(`${apiUrl}/utility/track-visit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(visitData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.success) {
      console.log('Visit tracked successfully:', result.data);
      return result.data;
    } else {
      console.error('Failed to track visit:', result.error);
      return null;
    }

  } catch (error) {
    console.error('Error tracking visit:', error);
    return null;
  }
};

/**
 * Track a page visit with authentication context
 */
export const trackAuthenticatedVisit = async (page, firebaseUser) => {
  const userId = firebaseUser?.uid || null;
  const userEmail = firebaseUser?.email || null;
  return trackVisit(page, userId, userEmail);
};

/**
 * Track a user action (clicks, signup events, etc.)
 */
export const trackUserAction = async (actionType, actionData = {}, firebaseUser = null) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || '';
    
    const payload = {
      actionType,
      page: window.location.pathname,
      userAgent: navigator.userAgent,
      sessionId: generateSessionId(),
      userId: firebaseUser?.uid || actionData.userId || null,
      userEmail: firebaseUser?.email || actionData.userEmail || null,
      ...actionData
    };

    const response = await fetch(`${apiUrl}/utility/track-action`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.success) {
      console.log('User action tracked:', actionType, result.data);
      return result.data;
    } else {
      console.error('Failed to track user action:', result.error);
      return null;
    }

  } catch (error) {
    console.error('Error tracking user action:', error);
    return null;
  }
};

/**
 * Track specific action types
 */
export const trackJoinClick = async (buttonText, page = window.location.pathname) => {
  return trackUserAction('join_click', { buttonText, page });
};

export const trackSignupStart = async (method = 'email') => {
  return trackUserAction('signup_start', { signupMethod: method });
};

export const trackSignupStep = async (step, method = 'email') => {
  return trackUserAction('signup_step', { signupStep: step, signupMethod: method });
};

export const trackSignupComplete = async (method = 'email') => {
  return trackUserAction('signup_complete', { signupMethod: method });
};

export const trackSignupAbandon = async (method = 'email', step = null, errorMessage = null, userData = {}) => {
  return trackUserAction('signup_abandon', { 
    signupMethod: method, 
    signupStep: step,
    errorMessage,
    abandonedEmail: userData.email || null,
    abandonedDisplayName: userData.displayName || null
  });
};

export const trackGuestPreviewShown = async () => {
  return trackUserAction('guest_preview_shown');
};

export const trackGuestPreviewStarted = async () => {
  return trackUserAction('guest_preview_started');
};

export const trackGuestPreviewMessageSent = async (messageCount = 1) => {
  return trackUserAction('guest_preview_message_sent', {
    metadata: {
      messageCount,
    },
  });
};

export const trackGuestPreviewWallShown = async (reason) => {
  return trackUserAction('guest_preview_wall_shown', {
    metadata: {
      reason,
    },
  });
};

export const trackGuestPreviewSignupClick = async (reason) => {
  return trackUserAction('guest_preview_signup_click', {
    metadata: {
      reason,
    },
  });
};

export const trackGuestPreviewLoginClick = async (reason) => {
  return trackUserAction('guest_preview_login_click', {
    metadata: {
      reason,
    },
  });
};

export const trackLoginPanelOpened = async (origin = 'hero') => {
  return trackUserAction('login_panel_opened', {
    metadata: {
      origin,
    },
  });
};

/**
 * Track login activity
 */
export const trackLoginAttempt = async (method = 'email', userId = null, success = true, errorMessage = null) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || '';
    
    // Detect device type
    const detectDeviceType = () => {
      const userAgent = navigator.userAgent;
      if (/tablet|ipad|playbook|silk/i.test(userAgent)) return 'tablet';
      if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(userAgent)) return 'mobile';
      return 'desktop';
    };

    // Detect browser
    const detectBrowser = () => {
      const userAgent = navigator.userAgent;
      if (userAgent.includes('Chrome')) return 'Chrome';
      if (userAgent.includes('Firefox')) return 'Firefox';
      if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
      if (userAgent.includes('Edge')) return 'Edge';
      if (userAgent.includes('Opera')) return 'Opera';
      return 'Unknown';
    };

    const loginData = {
      userId,
      loginMethod: method,
      userAgent: navigator.userAgent,
      sessionId: generateSessionId(),
      success,
      errorMessage,
      deviceType: detectDeviceType(),
      browser: detectBrowser()
    };

    const response = await fetch(`${apiUrl}/utility/track-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(loginData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.success) {
      console.log('Login activity tracked:', method, result.data);
      return result.data;
    } else {
      console.error('Failed to track login activity:', result.error);
      return null;
    }

  } catch (error) {
    console.error('Error tracking login activity:', error);
    return null;
  }
};

/**
 * Track successful login
 */
export const trackLoginSuccess = async (method = 'email', userId = null) => {
  return trackLoginAttempt(method, userId, true);
};

/**
 * Track failed login
 */
export const trackLoginFailure = async (method = 'email', errorMessage = null, userId = null) => {
  return trackLoginAttempt(method, userId, false, errorMessage);
};

/**
 * Get visit statistics (admin only)
 */
export const getVisitStats = async (options = {}, authToken = null) => {
  console.log('🌐 visitService.getVisitStats: Starting request with options:', options);
  console.log('🔑 visitService.getVisitStats: Auth token provided:', !!authToken, 'length:', authToken?.length);
  
  try {
    const apiUrl = process.env.REACT_APP_API_URL || '';
    console.log('🔗 visitService.getVisitStats: API URL:', apiUrl);
    
    const queryParams = new URLSearchParams();
    if (options.startDate) queryParams.append('startDate', options.startDate);
    if (options.endDate) queryParams.append('endDate', options.endDate);
    if (options.limit) queryParams.append('limit', options.limit.toString());
    if (options.pageType) queryParams.append('pageType', options.pageType); // Updated to use pageType
    if (options.lastVisitId) queryParams.append('lastVisitId', options.lastVisitId); // Add cursor support
    if (options.environment) queryParams.append('environment', options.environment); // Add environment filter

    const fullUrl = `${apiUrl}/utility/visit-stats?${queryParams}`;
    console.log('📍 visitService.getVisitStats: Full URL:', fullUrl);
    console.log('🔢 visitService.getVisitStats: Query params:', Object.fromEntries(queryParams));

    const headers = {
      'Content-Type': 'application/json',
    };

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    console.log('📋 visitService.getVisitStats: Request headers:', Object.keys(headers));

    const response = await fetch(fullUrl, {
      method: 'GET',
      headers
    });

    console.log('📨 visitService.getVisitStats: Response received');
    console.log('📊 visitService.getVisitStats: Response status:', response.status);
    console.log('✅ visitService.getVisitStats: Response ok:', response.ok);
    console.log('📋 visitService.getVisitStats: Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorText = await response.text();
      console.log('❌ visitService.getVisitStats: Error response body:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const result = await response.json();
    console.log('📦 visitService.getVisitStats: Parsed result:', {
      success: result.success,
      dataKeys: result.data ? Object.keys(result.data) : 'no data',
      visitsCount: result.data?.visits?.length,
      hasMore: result.data?.pagination?.hasMore
    });
    
    if (result.success) {
      return result.data;
    } else {
      console.log('❌ visitService.getVisitStats: API returned success=false:', result.error);
      throw new Error(result.error);
    }

  } catch (error) {
    console.error('💥 visitService.getVisitStats: Error occurred:', error);
    console.error('💥 visitService.getVisitStats: Error stack:', error.stack);
    throw error;
  }
};

/**
 * Get user action statistics (admin only)
 */
export const getUserActionStats = async (options = {}, authToken = null) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || '';
    
    const queryParams = new URLSearchParams();
    if (options.startDate) queryParams.append('startDate', options.startDate);
    if (options.endDate) queryParams.append('endDate', options.endDate);
    if (options.actionType) queryParams.append('actionType', options.actionType);
    if (options.environment) queryParams.append('environment', options.environment);
    if (options.lastActionId) queryParams.append('lastActionId', options.lastActionId);
    if (options.limit) queryParams.append('limit', options.limit.toString());

    const headers = {
      'Content-Type': 'application/json',
    };

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(`${apiUrl}/utility/action-stats?${queryParams}`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.error);
    }

  } catch (error) {
    console.error('Error getting user action stats:', error);
    throw error;
  }
};

/**
 * Get login activity statistics (admin only)
 */
export const getLoginActivityStats = async (options = {}, authToken = null) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || '';
    
    const queryParams = new URLSearchParams();
    if (options.startDate) queryParams.append('startDate', options.startDate);
    if (options.endDate) queryParams.append('endDate', options.endDate);
    if (options.userId) queryParams.append('userId', options.userId);
    if (options.loginMethod) queryParams.append('loginMethod', options.loginMethod);
    if (options.limit) queryParams.append('limit', options.limit.toString());

    const headers = {
      'Content-Type': 'application/json',
    };

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(`${apiUrl}/utility/login-stats?${queryParams}`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.error);
    }

  } catch (error) {
    console.error('Error getting login activity stats:', error);
    throw error;
  }
};